import type { Direction, Turn } from './direction';
import type { CritterKind } from './critters';
import type { MowResult } from './grid';
import type { PickupKind } from './level';
import type { BoostId } from './boosts';

/**
 * Facts the sim reports each tick. The renderer, audio and haptics react to these; they never
 * inspect sim internals to detect changes.
 */
type SimEventBody =
  /** `fresh`: a blade counted toward completion was cut (result 'cut'). */
  | {
      type: 'tileMowed';
      tick: number;
      x: number;
      y: number;
      dir: Direction;
      fresh: boolean;
      result: Exclude<MowResult, 'none'>;
      /** It was a molehill the mower flattened. */
      flattened?: true;
    }
  | { type: 'trampled'; tick: number; x: number; y: number }
  /** The mower drove through a lawn item it is strong enough to break (`tier`: its break tier). */
  | { type: 'smashed'; tick: number; x: number; y: number; tier: number }
  /** A secret yard's gate gave way (smashed or plowed through): the way in, never billed. */
  | { type: 'gateOpened'; tick: number; x: number; y: number }
  /** The mower smashed a beehive: the swarm stings (`hpLeft`; no sting under the shield). */
  | { type: 'hiveSmashed'; tick: number; x: number; y: number; stung: boolean; hpLeft: number }
  /** A meadow patch left half mowed grew back (its mowed tiles are grass again). */
  | { type: 'regrew'; tick: number; tiles: readonly { x: number; y: number }[] }
  /** The mower ran over a garden hose and chewed it. */
  | { type: 'chewed'; tick: number; x: number; y: number }
  /** The mower ran over a sprinkler head and broke it (it stops spraying). */
  | { type: 'sprinklerBroken'; tick: number; x: number; y: number }
  /** `index` refers to the level's pickup list. */
  | {
      type: 'pickup';
      tick: number;
      kind: PickupKind;
      index: number;
      x: number;
      y: number;
      /** A gas can or repair kit the mower had no use for: it paid a coin instead. */
      spare?: true;
      /** Pulled in from its tile by the coin magnet (it flies to the mower). */
      pulled?: true;
    }
  | { type: 'outOfFuel'; tick: number; x: number; y: number }
  /** The tank ran dry and the Reserve Can filled it up again. */
  | { type: 'reserveCan'; tick: number; x: number; y: number }
  /** A thorn patch: the tire went flat, or Hardened Tires took the puncture (`saved`). */
  | { type: 'flatTire'; tick: number; x: number; y: number; saved: boolean }
  /** A dead tree starts to creak (the level's `falls[index]`): it will come down soon. */
  | { type: 'treeCreak'; tick: number; index: number }
  /** A dead tree came down: a log across `tiles`, a stump at (x, y). */
  | {
      type: 'treeFell';
      tick: number;
      index: number;
      x: number;
      y: number;
      dir: Direction;
      tiles: { x: number; y: number }[];
    }
  /** A whistle: a ball is about to roll from (x, y) heading `dir`. */
  | { type: 'ballWarning'; tick: number; x: number; y: number; dir: Direction }
  /** The ball hit the mower (a heart lost unless shielded or just bumped). */
  | { type: 'ballHit'; tick: number; x: number; y: number; hpLeft: number }
  /** The ball is out of play: it hit something, or the mower, at (x, y). */
  | { type: 'ballGone'; tick: number; x: number; y: number }
  /** A repair kit patched the flat tire. */
  | { type: 'tirePatched'; tick: number; x: number; y: number }
  | { type: 'turned'; tick: number; x: number; y: number; turn: Turn; dir: Direction }
  /** A buffered steering request expired. `rule`: the mower can't turn that way yet. */
  | { type: 'turnRejected'; tick: number; turn: Turn; reason: 'rule' | 'blocked' }
  /** The mower hit something while at (x, y) heading `dir`; `critter` if it bumped one. */
  | {
      type: 'crashed';
      tick: number;
      x: number;
      y: number;
      dir: Direction;
      hpLeft: number;
      critter?: CritterKind;
      /** It hit a lawn item it could have smashed with a stronger mower ("Too tough!"). */
      tooTough?: true;
      /** The Bumper took it: no hit lost (it still counts as a crash, e.g. for Flawless). */
      bumper?: true;
    }
  /**
   * A squirrel or rabbit the mower bumped poofed off the lawn in a white puff (rules 6; never
   * harmed): no crash. It comes back home after a while.
   */
  | { type: 'critterPoofed'; tick: number; index: number; kind: CritterKind; x: number; y: number }
  /** A critter dashed away, startled by the mower or bumped by it (it is never harmed). */
  | { type: 'critterShooed'; tick: number; index: number }
  | { type: 'mowerBroken'; tick: number; x: number; y: number }
  /** Boss storm: the rain caught the mower. */
  | { type: 'caught'; tick: number; x: number; y: number }
  /** Boss Hedge Maze: the garden party started before the maze was mowed. */
  | { type: 'late'; tick: number }
  /** Mower soccer: the ball was kicked (`dir`), squirted out sideways, or popped back to the spot. */
  | { type: 'kicked'; tick: number; x: number; y: number; dir: Direction; squirt: boolean }
  /** `seat`: in a soccer match, the seat the goal counts for (an own goal counts for the other). */
  | { type: 'goal'; tick: number; goals: number; x: number; y: number; seat?: number }
  | { type: 'ballReset'; tick: number; x: number; y: number }
  /** Mower soccer: the halftime whistle (`won`: enough goals). */
  | { type: 'halftime'; tick: number; won: boolean }
  /**
   * A boost was fired (and now runs for its duration). `fromBox`: a Turbo picked up from an item
   * box on this lawn, not one from the player's stock (src/sim/items.ts).
   */
  | { type: 'boostUsed'; tick: number; boost: BoostId; fromBox?: true }
  /** Moles: the lawn trembles where a molehill is about to come up. */
  | { type: 'moleWarning'; tick: number; x: number; y: number }
  /** Moles: a molehill came up (to be flattened). */
  | { type: 'molehill'; tick: number; x: number; y: number }
  /** Boss rival: the robot mowed a tile. */
  | { type: 'rivalMowed'; tick: number; x: number; y: number; dir: Direction }
  /** Boss rival: the robot has mowed half the lawn, so it can no longer be beaten. */
  | { type: 'outmowed'; tick: number }
  /**
   * Head to head: this seat's mower drove into another's way and turned back where it stood (no
   * heart lost; docs/HEAD_TO_HEAD.md).
   */
  | { type: 'bumped'; tick: number; x: number; y: number; dir: Direction }
  /**
   * Head to head: this seat's mower was bumped in the side and spins at (x, y), dizzy, for
   * `DIZZY_TICKS` (no heart lost).
   */
  | { type: 'dizzy'; tick: number; x: number; y: number }
  /**
   * Head to head: the match is over (`winner`: a seat, or null for a draw). `why`: more than half
   * the grass, the lawn done, or the other breaking down.
   */
  | {
      type: 'matchOver';
      tick: number;
      winner: number | null;
      why: 'half' | 'done' | 'brokeDown' | 'goals' | 'time' | 'sheep' | 'maze' | 'lead';
    }
  /**
   * Boss herding: the mower nudged a sheep a tile on (and maybe into the pen). A round-up match:
   * `pen` is the seat whose pen it went into.
   */
  | { type: 'nudged'; tick: number; index: number; penned: boolean; pen?: number }
  /** A broken or out-of-gas mower continued (see the `revive` intent). */
  | { type: 'revived'; tick: number; x: number; y: number }
  | { type: 'levelComplete'; tick: number }
  /** A tool went off: tiles it cleared and critters it shooed (indices). */
  | {
      type: 'toolUsed';
      tick: number;
      slot: number;
      id: string;
      cleared: { x: number; y: number }[];
      shooed: number[];
    }
  /** A tool request was refused (recharging or out of charges). */
  | { type: 'toolDenied'; tick: number; slot: number; reason: 'cooldown' | 'empty' };

export type SimEventType = SimEvent['type'];

/** Collects events during a tick; the caller drains them after stepping. */
/**
 * An event, with the seat whose mower it happened to on a head-to-head lawn (docs/HEAD_TO_HEAD.md;
 * absent on every other lawn).
 */
export type SimEvent = SimEventBody & { seat?: number };

export class EventQueue {
  private events: SimEvent[] = [];

  push(event: SimEvent): void {
    this.events.push(event);
  }

  /**
   * Marks the events pushed since `from` (the queue's size then) as `seat`'s, unless a seat was
   * already marked (one seat's bump can turn another back).
   */
  stampSeat(from: number, seat: number): void {
    for (let i = from; i < this.events.length; i++) this.events[i]!.seat ??= seat;
  }

  /** Returns all pending events and clears the queue. */
  drain(): SimEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  get size(): number {
    return this.events.length;
  }
}
